import { Grid } from '@material-ui/core';
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { buyerSellerSchema } from 'utilities/validation/schemas';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { PartyTypeValue } from '../../../../api/file/constants';
import { FileDataContext, FileDataContextInterface } from '../../../../hooks/FileDataContext';
import AgentNetDropdownSelector from '../../../../ui-kit/inputs/AgentNetDropdownSelector';
import { EntityType, getFileStatus, getMaritalStatus } from '../../../constants';
import { ICreateBuyerSellerRequest } from '../interfaces/CreateBuyerSellerRequest';
import { IGetLenderResponse } from '../interfaces/CreateLenderRequest';
import './AddPartyForm.scss';
import PartyTypeFormWithValidation from './PartyTypesFormWithBaseFormGroup';

interface AddPartyFormProperties<T extends ICreateBuyerSellerRequest> {
  value: T;
  onChange: (value: T) => void;
  handleClose?: () => void;
  showValidation: boolean;
  setIsValid: (errs: boolean) => void;
  hideErrorTitleNotification?: boolean;
  qaAttrPrefix?: string;
}
const BaseAddPartyForm = <T extends ICreateBuyerSellerRequest & IGetLenderResponse>({
  value,
  onChange,
  setIsValid: setIsFormValid,
  showValidation,
  hideErrorTitleNotification,
  qaAttrPrefix = '',
}: AddPartyFormProperties<T>): JSX.Element => {
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { fileData } = fileDataCtx;
  const { errors } = useForm();
  const currentPartyType = value.partyType;
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [isPartyTypeFormGroupValid, setIsPartyTypeFormGroupValid] = useState<boolean>(false);
  const [updateValidations, setUpdateValidations] = useState<boolean>(false);

  const [resetVisibility, setResetVisibility] = useState<boolean>(false);
  const defaultValue = useRef(value);

  const updateValidationData = async (partyFields: T, validationUpdate?: boolean) => {
    const errs = (await doValidate(partyFields, buyerSellerSchema)) || [];
    setIsFormValid(errs.length === 0 && isPartyTypeFormGroupValid);
    setValidationErrors(errs);
    if (validationUpdate) {
      setUpdateValidations(true);
    }
  };

  /* Handle  Form Validation */
  const onChangeWithValidation = async (partyFields: T, validationUpdate?: boolean) => {
    updateValidationData(partyFields, validationUpdate);
    if (onChange) onChange(partyFields);
  };

  useEffect(() => {
    if (showValidation) onChangeWithValidation({ ...value });
  }, [showValidation]);

  useEffect(() => {
    onChangeWithValidation(value);
  }, [isPartyTypeFormGroupValid]);

  /* Handle Partytype Change */
  const handlePartyTypeChange = (val: string) => {
    const newPartyType = val as PartyTypeValue;
    let tempValue;
    if (defaultValue.current?.partyType === newPartyType) {
      tempValue = { ...defaultValue.current, partyType: newPartyType };
    } else {
      tempValue = { ...value, partyType: newPartyType };
      if (tempValue.address) {
        tempValue.address = {
          address1: '',
          address2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        };
      }
    }
    const { individual, couple, entity, trustEstate, ...rest } = tempValue;
    if (newPartyType === PartyTypeValue.married) {
      onChangeWithValidation({ ...rest, couple } as T, true);
    } else if (newPartyType === PartyTypeValue.individual) {
      onChangeWithValidation({ ...rest, individual } as T, true);
    } else if (newPartyType === PartyTypeValue.entity) {
      onChangeWithValidation({ ...rest, entity } as T, true);
    } else if (newPartyType === PartyTypeValue.trust) {
      onChangeWithValidation({ ...rest, trustEstate } as T, true);
    } else {
      onChangeWithValidation({ ...value } as T);
    }
    setResetVisibility(true);
  };
  const displayMaritalDropdown = () => {
    if (currentPartyType === PartyTypeValue.married || currentPartyType === PartyTypeValue.individual) {
      const partyKey = currentPartyType === PartyTypeValue.married ? 'couple' : 'individual';

      return (
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            disabled={fileData?.fileStatus !== 'Open' || fileData?.serviceStatus?.isReopenedByProduct}
            data-testid={`${qaAttrPrefix}MaritalStatus`}
            name="maritalStatus"
            label="Marital Status"
            defaultValue={value.partyType === PartyTypeValue.individual ? PartyTypeValue.toBeDetermined : ''}
            options={getMaritalStatus}
            error={errors.maritalStatus}
            value={value.individual?.maritalStatus ?? value?.couple?.maritalStatus ?? ''}
            menuOption={(val) =>
              onChange({
                ...value,
                [partyKey]: { ...value[partyKey], maritalStatus: val },
              })
            }
            dropdowntype="outlined"
          />
        </Grid>
      );
    } else if (currentPartyType === PartyTypeValue.entity) {
      return (
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            disabled={fileData?.fileStatus !== 'Open' || fileData?.serviceStatus?.isReopenedByProduct}
            label="Entity Type"
            name="EntityType"
            data-testid={`${qaAttrPrefix}EntityType`}
            defaultValue=""
            options={EntityType}
            value={value?.entity?.entityType ?? ''}
            menuOption={(val: string) => {
              onChange({ ...value, entity: { ...value.entity, entityType: val === '' ? undefined : val } });
            }}
            dropdowntype="outlined"
          />
        </Grid>
      );
    }
  };

  const handlePartyTypeFormOnChange = useCallback((val: T) => onChangeWithValidation(val), [isPartyTypeFormGroupValid]);

  const handleResetVisibility = useCallback((newValue) => {
    setResetVisibility(newValue);
  }, []);

  const handleUpdateValidation = useCallback((newValue) => {
    setUpdateValidations(newValue);
  }, []);

  const handlePartyTypeFormValidation = useCallback((newValue) => {
    setIsPartyTypeFormGroupValid(newValue);
  }, []);

  return (
    <>
      <Grid container spacing={3} className="add-party-form-container">
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            disabled={fileData?.fileStatus !== 'Open' || fileData?.serviceStatus?.isReopenedByProduct}
            name="partyType"
            id="partyType"
            label="Party Type"
            defaultValue="Individual"
            options={getFileStatus}
            errs={validationErrors}
            showValidation={showValidation}
            required
            value={value.partyType ?? 'Individual'}
            menuOption={handlePartyTypeChange}
            dropdowntype="outlined"
            data-testid={`${qaAttrPrefix}PartyType`}
          />
        </Grid>
        {value.partyType && (
          <>
            {displayMaritalDropdown()}
            <Grid item xs={12}>
              <PartyTypeFormWithValidation
                value={value}
                qaAttrPrefix={qaAttrPrefix}
                resetVisibility={resetVisibility}
                showAllValidation={showValidation}
                updateValidations={updateValidations}
                onChange={handlePartyTypeFormOnChange}
                setResetVisibility={handleResetVisibility}
                setUpdateValidations={handleUpdateValidation}
                setIsFormValid={handlePartyTypeFormValidation}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

const AddPartyForm = memo(BaseAddPartyForm);

export default AddPartyForm;
